import { Component, HostListener, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import { PendingReviewsBanner } from '../../shared/components/pending-reviews-banner/pending-reviews-banner';
import {
  Dashboard as DashboardService,
  AccessRoleDocumentCount,
  DashboardBatchRow,
  DocumentSource,
  DashboardSummary,
  Insights
} from '../../core/services/dashboard';
import { Connector } from '../../core/services/connector';
import { IngestionRealtime } from '../../core/services/ingestion-realtime';
import { environment } from '../../../environments/environment';
import { batchStatusClass, idsEqual } from '../../shared/constants/batch-status';

interface DonutSegment {
  label: string;
  percentage: number;
  color: string;
  count?: number;
  offset: number;
}

interface AccessRoleCount {
  role: string;
  count: number;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, Sidebar, Navbar, ToastComponent, PendingReviewsBanner],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css'
})
export class Dashboard implements OnInit, OnDestroy {
  private dashboardService = inject(DashboardService);
  private connectorService = inject(Connector);
  private realtime = inject(IngestionRealtime);
  private router = inject(Router);
  private destroy$ = new Subject<void>();
  private joinedBatchIds = new Set<string>();
  private realtimeSubscribed = false;

  // Data signals
  batchesInProgress = signal<DashboardBatchRow[]>([]);
  connectedSources = signal<DocumentSource[]>([]);
  accessRolesByDocCount = signal<AccessRoleCount[]>([]);
  summary = signal<DashboardSummary | null>(null);
  insights = signal<Insights | null>(null);

  // Loading states
  sourcesLoading = signal(true);
  dataLoading = signal(false);
  private pendingLoads = 0;

  // Connect modal
  showConnectModal = signal(false);
  connectStep = signal<'list' | 'sharepoint-form' | 'success'>('list');
  connectingSharePoint = signal(false);
  sharePointUrl = '';
  connectError = '';

  // Filters (frontend-only filtering over already-loaded data)
  filterClassification = 'All';
  filterSource = 'All';
  filterStatus = 'All';
  batchSearch = '';

  private static readonly sourceIcons: [string, string][] = [
    ['sharepoint', '/assets/icons/sources/sharepoint.svg'],
    ['github', '/assets/icons/sources/github.svg'],
    ['outlook', '/assets/icons/sources/outlook.svg'],
    ['mailbox', '/assets/icons/sources/outlook.svg'],
    ['slack', '/assets/icons/sources/slack.svg'],
    ['teams', '/assets/icons/sources/teams.svg'],
    ['chat', '/assets/icons/sources/google-chat.svg'],
    ['jira', '/assets/icons/sources/jira.svg'],
    ['azure', '/assets/icons/sources/azure-boards.svg']
  ];

  sourceIcon(provider: string): string {
    const key = (provider ?? '').toLowerCase();
    const match = Dashboard.sourceIcons.find(([keyword]) => key.includes(keyword));
    return match?.[1] ?? '/assets/icons/sources/database.svg';
  }

  get classificationOptions(): string[] {
    const values = this.batchesInProgress()
      .map(b => b.classification)
      .filter((c): c is string => !!c);
    return Array.from(new Set(values));
  }

  get sourceOptions(): string[] {
    return Array.from(new Set(this.connectedSources().map(s => s.provider)));
  }

  get statusOptions(): string[] {
    const values = this.batchesInProgress().map(b => b.status);
    return Array.from(new Set(values));
  }

  get filteredBatches(): DashboardBatchRow[] {
    const query = this.batchSearch.trim().toLowerCase();
    return this.batchesInProgress().filter(b => {
      if (this.filterClassification !== 'All' && b.classification !== this.filterClassification) return false;
      if (this.filterStatus !== 'All' && b.status !== this.filterStatus) return false;
      if (query && !b.name.toLowerCase().includes(query)) return false;
      return true;
    });
  }

  get hasActiveFilters(): boolean {
    return this.filterClassification !== 'All'
      || this.filterSource !== 'All'
      || this.filterStatus !== 'All'
      || this.batchSearch.trim().length > 0;
  }

  get showSkeletons(): boolean {
    return this.dataLoading() && this.summary() === null && this.insights() === null;
  }

  get filteredSources(): DocumentSource[] {
    return this.connectedSources().filter(s =>
      this.filterSource === 'All' || s.provider === this.filterSource
    );
  }

  private startLoad(): void {
    this.pendingLoads++;
    this.dataLoading.set(true);
  }

  private endLoad(): void {
    this.pendingLoads = Math.max(0, this.pendingLoads - 1);
    if (this.pendingLoads === 0) this.dataLoading.set(false);
  }

  // Stat getters from real APIs
  get documentsProcessed(): number { return this.summary()?.totalDocuments ?? 0; }
  get activeBatches(): number { return this.summary()?.activeBatches ?? 0; }
  get subClassifications(): number { return this.summary()?.subClassificationCount ?? 0; }
  get evidenceSnippets(): number { return this.summary()?.evidenceSnippetCount ?? 0; }
  get accessRolesInUse(): number { return this.insights()?.distinctRoleCount ?? 0; }
  get batchesNeedingReview(): number { return this.summary()?.failedBatches ?? 0; }
  get todayDocuments(): number { return this.summary()?.todayDocuments ?? 0; }

  // Used inside donut chart center
  get classifiedDocuments(): number { return this.insights()?.classifiedDocumentCount ?? 0; }
  get totalBatches(): number { return this.summary()?.totalBatches ?? 0; }

  get hasSources(): boolean {
    return !this.sourcesLoading() && this.connectedSources().length > 0;
  }

  get hasNoSources(): boolean {
    return !this.sourcesLoading() && this.connectedSources().length === 0;
  }

  get classificationSummary(): DonutSegment[] {
    const ins = this.insights();
    if (!ins?.topClassifications?.length) return [];
    const total = ins.classifiedDocumentCount || 1;
    const palette = ['#EF4444', '#3B82F6', '#14B8A6', '#F97316', '#A855F7'];
    const segments = ins.topClassifications.slice(0, 5).map((c, i) => ({
      label: c.name,
      percentage: Math.round((c.count / total) * 100),
      color: palette[i] ?? '#94A3B8',
      count: c.count,
      offset: 0
    }));
    const used = segments.reduce((s, c) => s + c.percentage, 0);
    if (used < 100) {
      segments.push({
        label: 'Other',
        percentage: 100 - used,
        color: '#94A3B8',
        count: Math.max(0, total - segments.reduce((s, c) => s + (c.count ?? 0), 0)),
        offset: 0
      });
    }
    return this.withDonutOffsets(segments);
  }

  get batchStatusOverview(): DonutSegment[] {
    const s = this.summary();
    if (!s?.batchStatusOverview?.length) return [];
    const colorMap: Record<string, string> = {
      'Completed': '#22C55E', 'Processing': '#F59E0B', 'Ready': '#A855F7',
      'Draft': '#94A3B8', 'Failed': '#EF4444', 'Created': '#F97316',
      'Preparing': '#3B82F6'
    };
    return this.withDonutOffsets(s.batchStatusOverview.map(b => ({
      label: b.status,
      percentage: b.percentage,
      color: colorMap[b.status] ?? '#94A3B8',
      count: b.count,
      offset: 0
    })));
  }

  get accessRolesAxisMax(): number {
    const counts = this.accessRolesByDocCount().map(r => r.count);
    const max = counts.length ? Math.max(...counts) : 10;
    const step = Math.ceil(max / 4 / 5) * 5 || 5;
    return Math.ceil(max / step) * step;
  }

  get accessRolesAxis(): number[] {
    const axisMax = this.accessRolesAxisMax;
    const step = Math.ceil(axisMax / 4 / 5) * 5 || 5;
    const ticks: number[] = [];
    for (let i = 0; i <= axisMax; i += step) ticks.push(i);
    return ticks;
  }

  ngOnInit(): void {
    this.loadSources();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.joinedBatchIds.forEach(id => this.realtime.leaveBatch(id));
    this.joinedBatchIds.clear();
  }

  private loadSources(): void {
    this.sourcesLoading.set(true);
    this.dashboardService.getDocumentSources().subscribe({
      next: res => {
        const sources = res.data ?? [];
        this.connectedSources.set(sources);
        this.sourcesLoading.set(false);
        if (sources.length > 0) {
          this.loadDashboardData();
        }
      },
      error: () => this.sourcesLoading.set(false)
    });
  }

  private loadDashboardData(): void {
    this.loadBatches();
    this.loadAccessRoles();
    this.loadSummary();
    this.loadInsights();
    if (!this.realtimeSubscribed) {
      this.realtimeSubscribed = true;
      this.subscribeToRealtime();
    }
  }

  private loadBatches(): void {
    this.startLoad();
    this.dashboardService.getBatchesInProgress(6).subscribe({
      next: res => {
        this.batchesInProgress.set(res.data ?? []);
        this.syncBatchSubscriptions();
        this.endLoad();
      },
      error: () => this.endLoad()
    });
  }

  private loadAccessRoles(): void {
    this.startLoad();
    this.dashboardService.getAccessRolesByDocCount().subscribe({
      next: res => {
        this.accessRolesByDocCount.set(
          (res.data ?? []).map((r: AccessRoleDocumentCount) => ({
            role: r.roleName,
            count: r.documentCount
          }))
        );
        this.endLoad();
      },
      error: () => this.endLoad()
    });
  }

  private loadSummary(): void {
    this.startLoad();
    this.dashboardService.getSummary().subscribe({
      next: res => { this.summary.set(res.data); this.endLoad(); },
      error: () => this.endLoad()
    });
  }

  private loadInsights(): void {
    this.startLoad();
    this.dashboardService.getInsights().subscribe({
      next: res => { this.insights.set(res.data); this.endLoad(); },
      error: () => this.endLoad()
    });
  }

  private refreshSourcesSilently(): void {
    this.dashboardService.getDocumentSources().subscribe({
      next: res => this.connectedSources.set(res.data ?? [])
    });
  }

  // Connect modal methods
  openConnectModal(): void {
    this.connectStep.set('list');
    this.sharePointUrl = '';
    this.connectError = '';
    this.showConnectModal.set(true);
  }

  closeConnectModal(): void {
    if (this.connectingSharePoint()) return;
    this.showConnectModal.set(false);
    this.connectStep.set('list');
    this.sharePointUrl = '';
    this.connectError = '';
  }

  startSharePointConnect(): void {
    this.connectError = '';
    this.connectingSharePoint.set(true);
    this.connectorService.connectSharePoint(environment.sharePointUrl).subscribe({
      next: () => {
        this.connectingSharePoint.set(false);
        this.connectStep.set('success');
      },
      error: (err) => {
        this.connectingSharePoint.set(false);
        this.connectError = err?.error?.message || 'Failed to connect.';
      }
    });
  }

  connectSharePoint(): void {
    this.connectError = '';
    this.connectingSharePoint.set(true);
    this.connectorService.connectSharePoint(environment.sharePointUrl).subscribe({
      next: () => {
        this.connectingSharePoint.set(false);
        this.connectStep.set('success');
      },
      error: (err) => {
        this.connectingSharePoint.set(false);
        this.connectError = err?.error?.message || 'Failed to connect. Please check the URL and try again.';
      }
    });
  }

  continueToDashboard(): void {
    this.closeConnectModal();
    this.loadSources();
  }

  clearFilters(): void {
    this.filterClassification = 'All';
    this.filterSource = 'All';
    this.filterStatus = 'All';
    this.batchSearch = '';
  }

  openBatch(id: string): void {
    this.router.navigate(['/batch-details', id]);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.showConnectModal()) this.closeConnectModal();
  }

  goToDataSources(): void {
    this.closeConnectModal();
    this.router.navigate(['/connectors']);
  }

  private syncBatchSubscriptions(): void {
    const visibleIds = new Set(this.batchesInProgress().map(b => b.id));
    this.joinedBatchIds.forEach(id => {
      if (!visibleIds.has(id)) {
        this.realtime.leaveBatch(id);
        this.joinedBatchIds.delete(id);
      }
    });
    visibleIds.forEach(id => {
      if (!this.joinedBatchIds.has(id)) {
        this.joinedBatchIds.add(id);
        void this.realtime.joinBatch(id);
      }
    });
  }

  private subscribeToRealtime(): void {
    this.realtime.batchProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => {
        this.batchesInProgress.update(rows => rows.map(row =>
          idsEqual(row.id, payload.batchId)
            ? { ...row, progress: payload.percentComplete ?? row.progress, documents: payload.total ?? row.documents, updatedAt: new Date().toISOString() }
            : row));
      });

    this.realtime.batchCompleted$
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => {
        this.batchesInProgress.update(rows => rows.map(row =>
          idsEqual(row.id, payload.batchId)
            ? { ...row, status: payload.status || row.status, updatedAt: payload.completedAt ?? new Date().toISOString() }
            : row));
        this.loadBatches();
        this.refreshSourcesSilently();
        this.loadSummary();
        this.loadInsights();
      });

    this.realtime.batchStarted$
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => {
        if (!this.batchesInProgress().some(row => idsEqual(row.id, payload.batchId))) {
          this.loadBatches();
        }
      });
  }

  formatDocCount(count: number): string {
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, '')}m docs`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1).replace(/\.0$/, '')}k docs`;
    return `${count} docs`;
  }

  timeAgo(iso: string): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '—';
    const minutes = Math.floor((Date.now() - then) / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }

  private withDonutOffsets(items: DonutSegment[]): DonutSegment[] {
    let cumulative = 0;
    return items.map(item => {
      const seg = { ...item, offset: cumulative };
      cumulative += item.percentage;
      return seg;
    });
  }

  statusBadgeClass(status: string): string {
    return batchStatusClass(status);
  }

  progressFillClass(status: string): string {
    switch (status) {
      case 'Completed':
      case 'Processed':
        return 'fill-completed';
      case 'Failed':
        return 'fill-failed';
      case 'Preparing':
      case 'Enumerating':
        return 'fill-preparing';
      case 'Draft':
      case 'Created':
        return 'fill-default';
      default:
        return 'fill-processing';
    }
  }

  sourceStatusClass(status: string): string {
    const key = (status ?? '').toLowerCase();
    if (key === 'syncing' || key === 'processing') return 'syncing';
    if (key === 'error' || key === 'failed') return 'error';
    return 'ready';
  }

  formatTimestamp(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString();
  }
}
